import { euclideanDistance } from '@/modules/Editor/algebra'

import type { Pt } from './geometry'
import { SLICE_HINT } from './messages'
import type { Stroke } from './strokeGesture'
import { resolveStroke, TOLERANCES_PX } from './strokeGesture'

export type SlicePreview<P> = {
  points: Pt[]
  closed: boolean
  plan: P | null
  error: string | null
}

export type SliceSession<P> = {
  plan(stroke: Stroke): P | { error: string }
  // isCurrent must be checked after every await and before any mutation
  apply(plan: P, isCurrent: () => boolean): Promise<boolean>
  draw(preview: SlicePreview<P> | null): void
  isStillValid(): boolean
  // calls onInvalid when the target stops being sliceable and onViewChange when the camera
  // moves; returns the unsubscribe
  watch?(onInvalid: () => void, onViewChange?: () => void): () => void
}

export type SliceControllerDeps<P> = {
  session: SliceSession<P>
  scale(): number
  hint(text: string | null): void
  warn(text: string): void
  finish(): void
  schedule?(fn: () => void): void
  delay?(fn: () => void, ms: number): () => void
  now?(): number
}

type Mode = 'armed' | 'freehand' | 'points' | 'applying' | 'done'

const FRAME_BUDGET_MS = 16
const IDLE_MS = 100
const DOUBLE_CLICK_MS = 300
const FLASH_MS = 1000

const isError = <P>(result: P | { error: string }): result is { error: string } =>
  typeof result === 'object' && result !== null && 'error' in result

const snap45 = (from: Pt, to: Pt): Pt => {
  const length = euclideanDistance(from, to)
  const step = Math.PI / 4
  const angle = Math.round(Math.atan2(to.y - from.y, to.x - from.x) / step) * step
  return { x: from.x + length * Math.cos(angle), y: from.y + length * Math.sin(angle) }
}

export class SliceController<P> {
  private mode: Mode = 'armed'
  private points: Pt[] = []
  private hover: Pt | null = null
  private downAt: Pt | null = null
  private closedByClick = false
  private lastClickAt = -Infinity
  // the raw pointer position, since a shift-snapped vertex can be far from where the click was
  private lastClickPoint: Pt | null = null
  private lastPlanMs = 0
  private framePending = false
  private cancelIdle: (() => void) | null = null
  private cancelFlash: (() => void) | null = null
  private unwatch: (() => void) | null = null
  private staleWhileApplying = false
  private lastPreview: SlicePreview<P> | null = null

  constructor(private readonly deps: SliceControllerDeps<P>) {
    deps.hint(SLICE_HINT)
    this.unwatch =
      deps.session.watch?.(
        () => this.invalidate(),
        () => this.redraw(),
      ) ?? null
  }

  get isActive(): boolean {
    return this.mode !== 'done'
  }

  pointerDown(p: Pt, shift: boolean): void {
    if (!this.guard() || this.mode === 'applying') {
      return
    }
    this.stopFlash()
    if (this.mode === 'armed') {
      this.downAt = p
      this.points = [p]
      return
    }
    if (this.mode !== 'points') {
      return
    }
    const now = this.now()
    const last = this.points[this.points.length - 1]
    if (
      now - this.lastClickAt < DOUBLE_CLICK_MS &&
      this.lastClickPoint &&
      euclideanDistance(p, this.lastClickPoint) < this.imagePx(TOLERANCES_PX.freehandStart)
    ) {
      void this.commit()
      return
    }
    this.lastClickAt = now
    this.lastClickPoint = p
    if (
      this.points.length >= 3 &&
      euclideanDistance(p, this.points[0]) < this.imagePx(TOLERANCES_PX.firstPoint)
    ) {
      this.closedByClick = true
      void this.commit()
      return
    }
    this.points.push(shift ? snap45(last, p) : p)
    this.requestPreview()
  }

  pointerMove(p: Pt, shift: boolean): void {
    if (
      this.mode === 'armed' &&
      this.downAt &&
      euclideanDistance(p, this.downAt) > this.imagePx(TOLERANCES_PX.freehandStart)
    ) {
      this.mode = 'freehand'
    }
    if (this.mode === 'freehand') {
      if (euclideanDistance(p, this.points[this.points.length - 1]) > this.imagePx(1)) {
        this.points.push(p)
      }
    } else if (this.mode === 'points') {
      this.hover = shift ? snap45(this.points[this.points.length - 1], p) : p
    } else {
      return
    }
    this.requestPreview()
  }

  pointerUp(): void {
    if (this.mode === 'freehand') {
      this.downAt = null
      void this.commit()
      return
    }
    if (this.mode === 'armed' && this.downAt) {
      this.lastClickPoint = this.downAt
      this.downAt = null
      this.mode = 'points'
      this.lastClickAt = this.now()
    }
  }

  removeLastPoint(): void {
    if (this.mode !== 'points') {
      return
    }
    if (this.points.length > 1) {
      this.points.pop()
      this.requestPreview()
      return
    }
    this.resetStroke()
  }

  cancel(): void {
    if (this.mode === 'done') {
      return
    }
    this.mode = 'done'
    this.unwatch?.()
    this.unwatch = null
    this.cancelIdle?.()
    this.stopFlash()
    this.draw(null)
    this.deps.hint(null)
    this.deps.finish()
  }

  async commit(): Promise<void> {
    if (this.mode !== 'freehand' && this.mode !== 'points') {
      return
    }
    if (!this.guard()) {
      return
    }
    const stroke = resolveStroke({
      points: this.points,
      closedByClick: this.closedByClick,
      scale: this.deps.scale(),
    })
    if (stroke === null) {
      this.resetStroke()
      return
    }
    if (isError(stroke)) {
      return this.reject(stroke.error)
    }
    const plan = this.deps.session.plan(stroke)
    if (isError(plan)) {
      return this.reject(plan.error)
    }
    this.mode = 'applying'
    this.staleWhileApplying = false
    this.cancelIdle?.()
    const applied = await this.deps.session.apply(
      plan,
      () =>
        this.mode === 'applying' && !this.staleWhileApplying && this.deps.session.isStillValid(),
    )
    if (this.mode !== 'applying') {
      return
    }
    if (!applied && this.staleWhileApplying) {
      this.cancel()
      return
    }
    if (applied) {
      this.mode = 'done'
      this.unwatch?.()
      this.unwatch = null
      this.draw(null)
      this.deps.hint(null)
      this.deps.finish()
      return
    }
    this.mode = 'armed'
    this.resetStroke()
  }

  // While applying, an invalidation marks the slice stale instead of cancelling: apply checks
  // isCurrent after its only await, before the synchronous mutations whose own events land here.
  private invalidate(): void {
    if (this.mode === 'applying') {
      this.staleWhileApplying = true
      return
    }
    this.cancel()
  }

  private draw(preview: SlicePreview<P> | null): void {
    this.lastPreview = preview
    this.deps.session.draw(preview)
  }

  // The preview is drawn in canvas coordinates, so a zoom or pan must redraw it.
  private redraw(): void {
    if (this.mode !== 'done' && this.lastPreview) {
      this.deps.session.draw(this.lastPreview)
    }
  }

  private now(): number {
    return this.deps.now ? this.deps.now() : performance.now()
  }

  private imagePx(screenPx: number): number {
    return screenPx / this.deps.scale()
  }

  private schedule(fn: () => void): void {
    if (this.deps.schedule) {
      this.deps.schedule(fn)
      return
    }
    requestAnimationFrame(fn)
  }

  private delay(fn: () => void, ms: number): () => void {
    if (this.deps.delay) {
      return this.deps.delay(fn, ms)
    }
    const id = setTimeout(fn, ms)
    return () => clearTimeout(id)
  }

  private guard(): boolean {
    if (this.mode === 'done') {
      return false
    }
    if (this.deps.session.isStillValid()) {
      return true
    }
    this.cancel()
    return false
  }

  private stopFlash(): void {
    this.cancelFlash?.()
    this.cancelFlash = null
  }

  private reject(error: string): void {
    this.deps.warn(error)
    this.draw({ points: this.points, closed: this.closedByClick, plan: null, error })
    this.resetStroke(false)
    this.cancelFlash = this.delay(() => {
      this.cancelFlash = null
      if (this.mode === 'armed' && !this.points.length) {
        this.draw(null)
      }
    }, FLASH_MS)
  }

  private resetStroke(clear = true): void {
    if (this.mode !== 'done') {
      this.mode = 'armed'
    }
    this.points = []
    this.hover = null
    this.downAt = null
    this.closedByClick = false
    if (clear) {
      this.draw(null)
    }
    this.deps.hint(SLICE_HINT)
  }

  // Large masks can take longer than a frame to plan; then the preview waits for a pause.
  private requestPreview(): void {
    if (this.lastPlanMs > FRAME_BUDGET_MS) {
      this.cancelIdle?.()
      this.cancelIdle = this.delay(() => {
        this.cancelIdle = null
        this.preview()
      }, IDLE_MS)
      return
    }
    if (this.framePending) {
      return
    }
    this.framePending = true
    this.schedule(() => {
      this.framePending = false
      this.preview()
    })
  }

  // The whole pass is timed (stroke validation included, early returns too), so expensive
  // previews switch to waiting for a pause.
  private preview(): void {
    if (this.mode !== 'freehand' && this.mode !== 'points') {
      return
    }
    const started = this.now()
    try {
      this.renderPreview()
    } finally {
      this.lastPlanMs = this.now() - started
    }
  }

  private renderPreview(): void {
    // hovering the first point previews what a click there does: close the lasso
    const closing =
      this.mode === 'points' &&
      this.hover !== null &&
      this.points.length >= 3 &&
      euclideanDistance(this.hover, this.points[0]) < this.imagePx(TOLERANCES_PX.firstPoint)
    const points = this.hover && !closing ? [...this.points, this.hover] : this.points
    const stroke = resolveStroke({ points, closedByClick: closing, scale: this.deps.scale() })
    if (stroke === null) {
      this.draw({ points, closed: false, plan: null, error: null })
      return
    }
    if (isError(stroke)) {
      this.deps.hint(stroke.error)
      this.draw({ points, closed: false, plan: null, error: stroke.error })
      return
    }
    const plan = this.deps.session.plan(stroke)
    const closed = stroke.kind === 'lasso'
    if (isError(plan)) {
      this.deps.hint(plan.error)
      this.draw({ points: stroke.points, closed, plan: null, error: plan.error })
      return
    }
    this.deps.hint(SLICE_HINT)
    this.draw({ points: stroke.points, closed, plan, error: null })
  }
}
